<script setup>
/**
 * @todo When legacy theme is removed: delete legacyProps, legacySizeMap, and the useTheme import.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrSkeleton from './HrSkeleton.vue';

/**
 * Circular container for profile photos, icons, and text initials.
 *
 * Three content modes — mutually exclusive, checked in this order:
 *   src  → profile photo (q-img fills the container)
 *   icon → icon-only avatar (passed to q-avatar)
 *   slot → custom content (text initials, arbitrary elements)
 *
 * @status recommended
 * @group media
 * @useWhen Representing a person with their photo, an icon, or text initials
 * @useWhen Displaying an actor in activity feeds, lists, or comment threads
 * @avoidWhen You need a non-circular image — use HrImg
 * @related HrImg
 * @a11y Decorative by default; callers should add aria-label when the avatar conveys meaning.
 * @see https://quasar.dev/vue-components/avatar Quasar q-avatar API
 */
defineOptions({ name: 'HrAvatar' });

const legacySizeMap = {
  xs: '24px',
  sm: '32px',
  md: '48px',
  lg: '64px',
  xl: '96px',
};

const props = defineProps({
  /**
   * Profile photo URL. When provided, a q-img fills the avatar container.
   * @fieldType image
   */
  src: {
    type: String,
    default: null,
  },
  /**
   * Material or FA icon name. Renders an icon-only avatar.
   * Ignored when `src` is set.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Semantic size — maps to --app-comp-avatar-size-* tokens.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Renders a circle skeleton placeholder instead of the avatar.
   */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar visual/behavioral props for the Legacy theme; dropped under the design-system theme.
   * @deprecated transitional — removed when the Legacy theme is removed
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    ...(props.src === null && props.icon !== null && { icon: props.icon }),
  };
  if (hasTheme.value) return { ...base, ...attrs };
  return { ...base, size: legacySizeMap[props.size], ...props.legacyProps, ...attrs };
});
</script>

<template>
  <hr-skeleton
    v-if="props.pending"
    v-bind="attrs"
    type="circle"
    :width="`var(--app-comp-avatar-size-${props.size})`"
    :height="`var(--app-comp-avatar-size-${props.size})`"
  />
  <q-avatar
    v-else
    v-bind="effectiveProps"
    :class="hasTheme ? ['hr-avatar', `hr-avatar--size-${props.size}`] : undefined"
  >
    <q-img
      v-if="props.src !== null"
      :src="props.src"
    />
    <!-- @slot Custom content rendered when neither src nor icon is provided — typically text initials. -->
    <slot v-else />
  </q-avatar>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-avatar {
  width: var(--app-comp-avatar-size-md);
  height: var(--app-comp-avatar-size-md);
  background: var(--app-surface-variant);
  color: var(--app-on-surface-variant);
  border-radius: var(--app-shape-corner-full);
}

.hr-avatar--size-xs {
  width: var(--app-comp-avatar-size-xs);
  height: var(--app-comp-avatar-size-xs);
}

.hr-avatar--size-sm {
  width: var(--app-comp-avatar-size-sm);
  height: var(--app-comp-avatar-size-sm);
}

.hr-avatar--size-lg {
  width: var(--app-comp-avatar-size-lg);
  height: var(--app-comp-avatar-size-lg);
}

.hr-avatar--size-xl {
  width: var(--app-comp-avatar-size-xl);
  height: var(--app-comp-avatar-size-xl);
}

</style>
